// The session client: the app's one data path. It owns a session store
// (the truth), a persistence (the cache it opens from), and a transport
// factory — one fresh transport per connection attempt. On a drop it
// reconnects with since=<last applied seq>: the server answers the
// deltas after it, or a snapshot when the sequence expired (the gap
// case), which the store takes as the whole truth on its own.
//
// Turn traffic (transcript, reply, audio, insert, draft, needs,
// finished, error, meeting, file) is not state: every Down frame is
// handed to onFrame listeners, and the screens (PRODUCT-321+) build on
// that. Commands go Up as the contract says, and typed text is in the
// history optimistically the moment it is sent.
import { createSignal } from "solid-js";
import { create } from "@bufbuild/protobuf";
import { TurnMode, UpSchema, type Down, type Up } from "@/gen/psypen/v1/session_pb";
import { createSessionStore, localSaid, type SessionStore } from "@/session/store";
import type { SessionPersistence } from "@/session/persist";
import type { Transport } from "@/session/transport";
import type { SessionState } from "@/session/types";

export type SessionStatus =
  // Opened from the cache, no socket yet this run.
  | "cached"
  | "connecting"
  | "live"
  // A drop, waiting out the backoff to dial again.
  | "reconnecting"
  // The transport factory could not build a socket (no token to dial
  // with): nothing retries until a fresh sign-in reloads the page.
  | "signedout"
  // Closed on purpose; it stays closed.
  | "closed";

export interface SessionOptions {
  // A fresh transport per connection attempt (the fake and the ws one
  // are both one socket each).
  makeTransport: () => Transport;
  persist?: SessionPersistence;
  ver?: string;
  os?: string;
  // Milliseconds to wait before attempt N (1-based after the first
  // drop); default doubles from 1s, capped at 15s. Tests pass () => 0.
  backoff?: (attempt: number) => number;
}

function defaultBackoff(attempt: number): number {
  return Math.min(1000 * 2 ** (attempt - 1), 15_000);
}

// The cache takes a plain copy of the state: the store hands out its
// reactive proxy, which IndexedDB's structured clone refuses
// (DataCloneError — the cache silently never persisted until this
// copy, found live 2026-10-10). The state's types are JSON-safe by
// design, so the round-trip loses nothing.
function plain(state: SessionState): SessionState {
  return JSON.parse(JSON.stringify(state)) as SessionState;
}

export interface Session {
  status(): SessionStatus;
  state(): SessionState;
  // The store the state lives in, for the paths that write it
  // optimistically off the socket (the gate's answer) — the socket's
  // own word still settles everything it says.
  store: SessionStore;
  // The last applied stream sequence, for a resume; undefined before the
  // first snapshot.
  seq(): bigint | undefined;
  send(u: Up): boolean;
  // Typed text as its own turn: TurnStart + TypedText on the socket, a
  // local history entry immediately. Returns the turn id, or null when
  // there is no socket to carry it.
  sendTyped(text: string, mode?: TurnMode): string | null;
  cancelTurn(turn: string): boolean;
  onFrame(cb: (d: Down) => void): () => void;
  close(): void;
}

export function createSession(opts: SessionOptions): Session {
  const store = createSessionStore();
  const [status, setStatus] = createSignal<SessionStatus>("connecting");
  const frameListeners = new Set<(d: Down) => void>();
  let current: Transport | null = null;
  let attempt = 0;
  let closed = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  const backoff = opts.backoff ?? defaultBackoff;

  function connect(): void {
    if (closed) return;
    attempt += 1;
    let t: Transport;
    try {
      t = opts.makeTransport();
    } catch (err) {
      // A factory that cannot build a socket (no token to dial with)
      // stops the loop on purpose: retrying cannot help until the page
      // is signed in again, which is a full navigation.
      console.warn("session: no transport:", err);
      setStatus("signedout");
      return;
    }
    current = t;
    const since = store.state.seq !== "" ? BigInt(store.state.seq) : undefined;
    t.connect({
      since,
      onOpen() {
        // A stale socket's events (one the reconnect already replaced)
        // never touch the state.
        if (t !== current || closed) return;
        setStatus("live");
      },
      onDown(d) {
        if (t !== current || closed) return;
        store.applyDown(d);
        opts.persist?.save(plain(store.state));
        for (const cb of frameListeners) cb(d);
      },
      onClose(err) {
        if (t !== current) return;
        if (closed) {
          setStatus("closed");
          return;
        }
        if (err) console.warn("session: the socket dropped:", err.message);
        setStatus("reconnecting");
        // Attempt 1 after the first drop, 2 after the second, …
        const wait = backoff(Math.max(1, attempt - 1));
        reconnectTimer = setTimeout(connect, wait);
      },
    });
  }

  // Boot: the cache first, so the app opens in its last state before the
  // network answers; the first socket frame settles what the cache said.
  function boot(): void {
    const p = opts.persist?.load();
    if (p) {
      p.then((saved) => {
        if (closed) return;
        if (saved) {
          store.restore(saved);
          setStatus("cached");
        }
        connect();
      }).catch(() => connect());
    } else {
      connect();
    }
  }

  function send(u: Up): boolean {
    if (status() !== "live" || !current) return false;
    try {
      current.send(u);
      return true;
    } catch (err) {
      console.warn("session: send failed:", err);
      return false;
    }
  }

  boot();

  return {
    status,
    state: () => store.state,
    store,
    seq() {
      const s = store.state.seq;
      return s === "" ? undefined : BigInt(s);
    },
    send,
    sendTyped(text, mode) {
      const turn = newTurnId();
      const start = create(UpSchema, {
        msg: {
          case: "turnStart",
          value: {
            turn,
            mode: mode ?? TurnMode.UNSPECIFIED,
            held: TurnMode.UNSPECIFIED,
          },
        },
      });
      if (!send(start)) return null;
      if (!send(create(UpSchema, { msg: { case: "text", value: { text } } }))) return null;
      store.applyLocal(localSaid(turn, text));
      opts.persist?.save(plain(store.state));
      return turn;
    },
    cancelTurn(turn) {
      return send(create(UpSchema, { msg: { case: "turnCancel", value: { turn } } }));
    },
    onFrame(cb) {
      frameListeners.add(cb);
      return () => frameListeners.delete(cb);
    },
    close() {
      if (closed) return;
      closed = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      current?.close();
      setStatus("closed");
    },
  };
}

// Turn ids are minted by the client and unique per user (the contract's
// TurnStart.turn); a random UUID is that on one device.
export function newTurnId(): string {
  const c = globalThis.crypto;
  if (c && "randomUUID" in c) return c.randomUUID();
  // A fallback for non-secure contexts without crypto.randomUUID.
  return `turn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
